import { NgForage } from 'ngforage';
import * as semaphore from 'semaphore';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import SemaphoreHelper from '../../../helpers/semaphore-helper';
import { NgForageTimestampService } from '../../ng-forage/ng-forage-timestamp.service';
import { OfflineStorageObject } from '../../placer-offline-changes/abstract/offline-storage-object';
import { ExpiredOfflineObjectAsyncTimestampRemover } from './expired-offline-object-async-timestamp-remover';

export abstract class AbstractOfflineStorageAsyncTimestampService<
  ID,
  O extends OfflineStorageObject<ID>,
  R extends ExpiredOfflineObjectAsyncTimestampRemover<ID, O>
> {
  public readonly offlineStorageMutex = semaphore(1);

  private remover: R;

  constructor(
    public readonly storageKeyPrefix: string,
    public readonly timestampCollectionKey: string,
    protected ngForage: NgForage,
    protected ngForageTimestampService: NgForageTimestampService
  ) {
    this.remover = this.instantiateRemover();
    this.remover.removeExpiredItems();
  }

  protected abstract instantiateRemover(): R;

  public getObjectStorageKey(id: ID): string {
    if (id == null) {
      throw new Error('id must not be null');
    }
    return this.storageKeyPrefix + this.stringifyID(id);
  }

  public abstract convertToID(o: any): ID;

  protected abstract stringifyID(id: ID): string;

  public async getObject(id: ID): Promise<O> {
    const objectStr = await this.ngForage.getItem<string>(
      this.getObjectStorageKey(id)
    );
    const object = JSON.parse(objectStr);
    if (object != null) {
      SemaphoreHelper.use(this.offlineStorageMutex, async () => {
        await this.ngForageTimestampService.setTimestamps(
          this.timestampCollectionKey,
          [{ key: id.toString(), timestamp: Date.now() }]
        );
      });
    }
    return this.processRetrievedObject(object);
  }

  protected async getObjectNoTimestampUpdate(id: ID): Promise<O> {
    const objectStr = await this.ngForage.getItem<string>(
      this.getObjectStorageKey(id)
    );
    const object = JSON.parse(objectStr);
    return this.processRetrievedObject(object);
  }

  public async getObjects(ids: ID[]): Promise<O[]> {
    if (ids == null) {
      return null;
    }
    if (ids.length === 0) {
      return [];
    }

    return this.getObjectsPromise(ids).then((objects) => {
      CollectionHelper.filterInPlace(objects, (o) => o != null);

      if (CollectionHelper.isPopulated(objects)) {
        SemaphoreHelper.use(this.offlineStorageMutex, async () => {
          const now = Date.now();
          await this.ngForageTimestampService.setTimestamps(
            this.timestampCollectionKey,
            objects.map((o) => {
              return {
                key: o.id.toString(),
                timestamp: now,
              };
            })
          );
        });
      }

      for (let i = 0; i < objects.length; i++) {
        objects[i] = this.processRetrievedObject(objects[i]);
      }

      return objects;
    });
  }

  private async getObjectsPromise(ids: ID[]): Promise<O[]> {
    return Promise.all(ids.map((id) => this.getObjectOrNull(id)));
  }

  private async getObjectsNoTimestampUpdate(ids: ID[]): Promise<O[]> {
    if (ids == null) {
      return null;
    }
    if (ids.length === 0) {
      return [];
    }

    return this.getObjectsPromise(ids).then((objects) => {
      CollectionHelper.filterInPlace(objects, (o) => o != null);

      for (let i = 0; i < objects.length; i++) {
        objects[i] = this.processRetrievedObject(objects[i]);
      }

      return objects;
    });
  }

  private async getObjectOrNull(id: ID): Promise<O> {
    if (id == null) {
      return null;
    }
    let object = await this.ngForage.getItem<string>(
      this.getObjectStorageKey(id)
    );
    return JSON.parse(object);
  }

  protected processRetrievedObject(object: O): O {
    return object;
  }

  public async storeObject(object: O): Promise<void> {
    this.verifyToStoreObject(object);
    object = this.preprocessToStoreObject(object);
    await SemaphoreHelper.use(this.offlineStorageMutex, async () => {
      await this.ngForage.setItem(
        this.getObjectStorageKey(object.id),
        JSON.stringify(object)
      );
      await this.ngForageTimestampService.setTimestamps(
        this.timestampCollectionKey,
        [
          {
            key: object.id.toString(),
            timestamp: Date.now(),
          },
        ]
      );
    });
  }

  public async storeObjects(objects: O[]): Promise<void> {
    if (!CollectionHelper.isPopulated(objects)) {
      return;
    }

    const objectsCopy: O[] = CollectionHelper.getNullArray(objects.length);
    for (let i = 0; i < objects.length; i++) {
      this.verifyToStoreObject(objects[i]);
      objectsCopy[i] = this.preprocessToStoreObject(objects[i]);
    }

    await SemaphoreHelper.use(this.offlineStorageMutex, async () => {
      const now = Date.now();
      const promises = [];
      objectsCopy.forEach((o) => {
        promises.push(
          this.ngForage.setItem(
            this.getObjectStorageKey(o.id),
            JSON.stringify(o)
          )
        );
      });
      promises.push(
        this.ngForageTimestampService.setTimestamps(
          this.timestampCollectionKey,
          objectsCopy.map((o) => {
            return {
              key: o.id.toString(),
              timestamp: now,
            };
          })
        )
      );
      await Promise.all(promises);
    });
  }

  protected abstract verifyToStoreObject(object: O): void;

  protected preprocessToStoreObject(object: O): O {
    return object;
  }

  public async removeObject(object: O): Promise<void> {
    if (object == null) {
      return;
    }
    SemaphoreHelper.use(this.offlineStorageMutex, async () => {
      const storageKey = this.getObjectStorageKey(object.id);
      const toRemoveStr = await this.ngForage.getItem<string>(storageKey);
      const toRemove = JSON.parse(toRemoveStr);
      if (toRemove != null) {
        this.cleanupOfflineObject(toRemove);
      }
      await this.ngForage.removeItem(storageKey);
      await this.ngForageTimestampService.removeItems(
        this.timestampCollectionKey,
        [this.stringifyID(object.id)]
      );
    });
  }

  public async removeObjectForID(id: ID): Promise<void> {
    if (id == null) {
      return;
    }
    SemaphoreHelper.use(this.offlineStorageMutex, async () => {
      const storageKey = this.getObjectStorageKey(id);
      const toRemoveStr = await this.ngForage.getItem<string>(storageKey);
      const toRemove = JSON.parse(toRemoveStr);
      if (toRemove == null) {
        return;
      }
      this.cleanupOfflineObject(toRemove);
      await this.ngForage.removeItem(storageKey);
      await this.ngForageTimestampService.removeItems(
        this.timestampCollectionKey,
        [this.stringifyID(id)]
      );
    });
  }

  public async removeObjects(objects: O[]): Promise<void> {
    if (!CollectionHelper.isPopulated(objects)) {
      return;
    }
    SemaphoreHelper.use(this.offlineStorageMutex, async () => {
      const ids = objects.map((o) => o.id);
      CollectionHelper.filterInPlace(ids, (id) => id != null);
      const toRemove = await this.getObjectsNoTimestampUpdate(ids);
      if (!CollectionHelper.isPopulated(toRemove)) {
        return;
      }
      const removalPromises: Promise<void>[] = [];
      const timestampKeys: string[] = [];
      toRemove.forEach((r) => {
        this.cleanupOfflineObject(r);
        const storageKey = this.getObjectStorageKey(r.id);
        removalPromises.push(this.ngForage.removeItem(storageKey));
        timestampKeys.push(this.stringifyID(r.id));
      });

      removalPromises.push(
        this.ngForageTimestampService.removeItems(
          this.timestampCollectionKey,
          timestampKeys
        )
      );
      await Promise.all(removalPromises);
    });
  }

  public abstract cleanupOfflineObject(object: O): Promise<void>;
}
